import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../../lib/api";
import { ActivityCard } from "./ActivityCard";
import { EmptyState } from "./EmptyState";

/**
 * ActivityFeed — generic feed component used by both Pulse and Security pages.
 *
 * Encapsulates loading/error/empty states. The parent provides only the
 * endpoint path; everything else is identical between the two channels.
 */
export const ActivityFeed = ({ endpoint, limit = 30, emptyTitleKey, emptyHintKey, "data-testid": testId = "ob-activity-feed" }) => {
    const { t } = useTranslation();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await api.get(`${endpoint}?limit=${limit}`);
                if (!cancelled) setEvents(res.data?.events || []);
            } catch (err) {
                if (!cancelled) setError(err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [endpoint, limit]);

    if (loading) {
        return (
            <p
                data-testid="ob-activity-loading"
                className="text-sm py-6"
                style={{ color: "var(--ob-text-tertiary)" }}
            >
                {t("ob.activity.loading")}
            </p>
        );
    }
    if (error) {
        return (
            <p
                data-testid="ob-activity-error"
                className="text-sm py-6"
                style={{ color: "var(--ob-text-tertiary)" }}
            >
                {t("ob.activity.error")}
            </p>
        );
    }
    if (events.length === 0) {
        return <EmptyState titleKey={emptyTitleKey} hintKey={emptyHintKey} />;
    }
    return (
        <div data-testid={testId} className="space-y-3">
            {events.map((e) => (
                <ActivityCard key={e.id} event={e} />
            ))}
        </div>
    );
};
